import { Facebook, Instagram, QrCode, MessageSquare, ArrowDown, ArrowRight, Share2, ShoppingBag } from "lucide-react";

/**
 * A quiet visual explainer for the /for-growers page.
 *
 *   ┌───────────────────────┐      ┌──────────────────────┐      ┌────────┐
 *   │ SELL HERE             │  →   │ SHARE YOUR GATE      │  →   │ RESULT │
 *   │ [FB post] [IG story]  │      │ gateandgather.co.nz/ │      │ Order  │
 *   │ [Market QR] [Text a…] │      │   g/your-gate        │      │        │
 *   └───────────────────────┘      └──────────────────────┘      └────────┘
 *
 * Groups the four channels inside a single SELL HERE tile so they visually
 * "belong together" rather than reading as four independent routes into the
 * Gate. Then one arrow → SHARE YOUR GATE (which shows a simple example URL) →
 * one arrow → RESULT / Order.
 *
 * Desktop: three tiles laid out horizontally with arrow separators.
 * Mobile:  three tiles stacked vertically with down-arrow separators.
 * Static, no animation, no new functionality.
 */
const CHANNELS = [
  { icon: Facebook,       label: "Facebook post",   testid: "funnel-channel-facebook" },
  { icon: Instagram,      label: "Instagram story", testid: "funnel-channel-instagram" },
  { icon: QrCode,         label: "Market QR",       testid: "funnel-channel-qr" },
  { icon: MessageSquare,  label: "Text to a regular", testid: "funnel-channel-text" },
];

function ChannelPill({ Icon, label, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs sm:text-sm text-stone-800"
    >
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#3F674E] shrink-0" strokeWidth={2} />
      <span className="leading-none whitespace-nowrap">{label}</span>
    </div>
  );
}

function SellHereTile({ variant }) {
  return (
    <div
      data-testid={`gate-funnel-sell-here-${variant}`}
      className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5"
    >
      <p className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#3F674E]">Sell here</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {CHANNELS.map((c) => (
          <ChannelPill key={c.label} Icon={c.icon} label={c.label} testid={`${c.testid}-${variant}`} />
        ))}
      </div>
    </div>
  );
}

function ShareGateTile({ variant }) {
  return (
    <div
      data-testid={`gate-funnel-share-gate-${variant}`}
      className="rounded-2xl border-2 border-[#3F674E]/25 bg-[#3F674E]/8 p-4 sm:p-5"
    >
      <div className="flex items-center gap-2">
        <Share2 className="w-3.5 h-3.5 text-[#3F674E]" strokeWidth={2.25} />
        <p className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#3F674E]">Share your Gate</p>
      </div>
      <p className="mt-3 font-mono text-[13px] sm:text-sm text-[#20251F] leading-snug break-all">
        gateandgather.co.nz/g/<span className="font-semibold text-[#3F674E]">your-gate</span>
      </p>
      <p className="mt-2 text-xs text-stone-600 leading-relaxed">
        One link goes everywhere.
      </p>
    </div>
  );
}

function ResultTile({ variant }) {
  return (
    <div
      data-testid={`gate-funnel-result-${variant}`}
      className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5 flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-full bg-[#20251F] text-white grid place-items-center shrink-0">
        <ShoppingBag className="w-5 h-5" strokeWidth={2} />
      </div>
      <div>
        <p className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#3F674E]/70">Result</p>
        <p className="font-serif-heading text-lg text-[#20251F] leading-tight">Order</p>
      </div>
    </div>
  );
}

export default function GateFunnel() {
  return (
    <section className="mt-8" data-testid="gate-funnel">
      <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">Sell where you already sell</p>
      <h2 className="font-serif-heading text-2xl sm:text-3xl tracking-tight text-[#20251F] mt-1 leading-tight" data-testid="gate-funnel-h2">
        Sell where you already sell. Send everyone to one Gate.
      </h2>
      <p className="mt-2 text-sm sm:text-[15px] text-stone-700 leading-relaxed max-w-2xl">
        Your Gate keeps what's available, your stock and your orders in one place.
      </p>

      {/* MOBILE + tablet stacked view — three tiles stacked with down-arrow
          separators. Hidden on md+ where the horizontal flow takes over. */}
      <div className="mt-6 md:hidden" data-testid="gate-funnel-mobile">
        <SellHereTile variant="m" />
        <div className="flex justify-center py-3" aria-hidden="true">
          <ArrowDown className="w-5 h-5 text-[#3F674E]" strokeWidth={2.25} />
        </div>
        <ShareGateTile variant="m" />
        <div className="flex justify-center py-3" aria-hidden="true">
          <ArrowDown className="w-5 h-5 text-[#3F674E]" strokeWidth={2.25} />
        </div>
        <ResultTile variant="m" />
      </div>

      {/* DESKTOP flow — three tiles side-by-side with arrow separators.
          Grid keeps the middle tile flexible while the sides stay compact. */}
      <div
        className="mt-6 hidden md:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,220px)] items-stretch gap-4"
        data-testid="gate-funnel-desktop"
      >
        <SellHereTile variant="d" />
        <div className="flex items-center justify-center text-[#3F674E]" aria-hidden="true">
          <ArrowRight className="w-6 h-6" strokeWidth={2.25} />
        </div>
        <ShareGateTile variant="d" />
        <div className="flex items-center justify-center text-[#3F674E]" aria-hidden="true">
          <ArrowRight className="w-6 h-6" strokeWidth={2.25} />
        </div>
        <ResultTile variant="d" />
      </div>

      <div className="mt-6 rounded-2xl border border-[#DED5C3] bg-[#FFFCF6] p-5" data-testid="gate-funnel-footnote">
        <p className="font-serif-heading text-lg text-[#20251F] leading-snug">Your Gate is the source of truth.</p>
        <p className="mt-1.5 text-sm text-stone-700 leading-relaxed">
          When stock changes, your Gate changes. You don't need to update every old post, message or QR code.
        </p>
      </div>
    </section>
  );
}
